import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

//assets
import arrowIcon from "../../../assets/previous_icon.png";
import deleteIcon from "../../../assets/delete_Icon.png";
import useLanguage from "../../../utils/useLanguage";
import CustomInputWithUnit from "../../../components/CustomInputWithUnit";
import CustomButton from "../../../components/CustomButton";
import CustomCheckbox from "../../../components/CustomCheckbox";

const SelectedProductOrderList = ({
  data = [],
  setSelectedItems,
  currency,
  selectedItems = {},
  onItemSelect,
  onInputChange,
  headers = [], // Array of header objects: { key, label, type, unit?, component? }
  groupByField = "code", // Field to group items by (e.g., 'code', 'category')
  itemsField = "colors", // Field that contains the items array (e.g., 'colors', 'variants')
  itemKeyField = "colorCode", // Field to use as item key (e.g., 'colorCode', 'id')
  groupLabelPrefix = "code", // Translation key for group label prefix
  showDeleteButton = true, // Whether to show delete button
  deleteButtonLabel = "delete", // Translation key for delete button
}) => {
  const { currentDirection } = useLanguage();
  const { t } = useTranslation("general");

  const [collapsed, setCollapsed] = useState({});

  /* HANDLER FUNCTIONS */
  const handleInputValueChange = (groupCode, itemKey, field, value) => {
    if (onInputChange) {
      onInputChange(groupCode, itemKey, field, value);
    }
  };

  const handleDeleteGroup = (groupCode) => {
    if (setSelectedItems) {
      setSelectedItems((prev) =>
        prev.filter((group) => group[groupByField] !== groupCode)
      );
    }

    // Also call onItemSelect to update the parent's selectedItems state
    if (onItemSelect) {
      onItemSelect(groupCode, null, false);
    }
  };

  const handleCheckboxChange = (groupCode, itemKey) => {
    // Call the parent's handler with the new state
    if (onItemSelect) {
      onItemSelect(groupCode, itemKey, !selectedItems[groupCode]?.[itemKey]);
    }
  };

  const toggleCollapse = (groupCode) => {
    setCollapsed((prev) => ({
      ...prev,
      [groupCode]: !prev[groupCode],
    }));
  };

  // Render field based on header configuration
  const renderField = (header, group, item) => {
    const { key, type, unit, component: CustomComponent } = header;

    if (CustomComponent) {
      return (
        <CustomComponent
          value={item[key] || ""}
          onChange={(e) =>
            handleInputValueChange(
              group[groupByField],
              item[itemKeyField],
              key,
              e.target.value
            )
          }
          unit={unit ? t(unit) : undefined}
          currency={currency}
        />
      );
    }

    switch (type) {
      case "checkbox":
        return (
          <CustomCheckbox
            checked={true} // Always checked in the selected items section
            onChange={() =>
              handleCheckboxChange(group[groupByField], item[itemKeyField])
            }
          />
        );

      case "input":
        return (
          <CustomInputWithUnit
            value={item[key] || ""}
            onChange={(e) =>
              handleInputValueChange(
                group[groupByField],
                item[itemKeyField],
                key,
                e.target.value
              )
            }
            unit={
              unit
                ? unit === "currency"
                  ? t(currency?.value || "USD")
                  : t(unit)
                : undefined
            }
          />
        );

      case "text":
      default:
        return <span>{item[key]}</span>;
    }
  };

  /* USEEFFECT */
  useEffect(() => {
    const initialCollapsed = {};
    data.forEach((group) => {
      initialCollapsed[group[groupByField]] = false; // Start expanded for selected items
    });
    setCollapsed(initialCollapsed);
  }, [data, groupByField]);

  return (
    <div
      className="c-fabric-table o-column o-column--gap-size_400 u-margin u-margin--bottom_700"
      dir={currentDirection}
    >
      {data.map((group) => (
        <div key={group[groupByField]} className="c-fabric-block">
          <div className="c-fabric-header o-row o-row--horizontal-align_space-between">
            <div onClick={() => toggleCollapse(group[groupByField])} className="c-fabric-header" style={{ border: 0, padding: 0 }}>
              <span  className="c-collapse-icon" style={{ cursor: "pointer" }}>
                <img
                  src={arrowIcon}
                  alt="Collapse"
                  style={{
                    width: 16,
                    transform: collapsed[group[groupByField]]
                      ? "rotate(0deg)"
                      : "rotate(-90deg)",
                    transition: "transform 0.3s ease",
                  }}
                />
              </span>
              <div className="o-column o-column--gap-size_200">
                <span className="c-fabric-code">
                  {t(groupLabelPrefix)}: {group[groupByField]}
                </span>
                {group.color && (
                  <span className="c-fabric-code">
                    {t("color")}: {group.color}
                  </span>
                )}
              </div>
            </div>

            {showDeleteButton && (
              <div className="o-row o-row--gap-size_300">
                <CustomButton
                  variant="red"
                  title={t(deleteButtonLabel)}
                  icon={deleteIcon}
                  iconSize={20}
                  onClick={() => handleDeleteGroup(group[groupByField])}
                />
              </div>
            )}
          </div>

          {!collapsed[group[groupByField]] && (
            <table className="c-fabric-inner-table">
              <thead>
                <tr>
                  {headers.map((header) => (
                    <th key={header.key}>{t(header.label)}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {group[itemsField]?.map((item) => (
                  <tr key={item[itemKeyField]}>
                    {headers.map((header) => (
                      <td key={header.key}>
                        {renderField(header, group, item)}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      ))}
    </div>
  );
};

export default SelectedProductOrderList;
